import {
  DOMParser as PMDomParser,
  DOMSerializer,
  Node as ProsemirrorNode,
  Schema,
} from 'prosemirror-model';

import { defaultSchemaFactory, RtSchema } from './schema';

export const SCHEMAS = {
  [RtSchema.Default]: defaultSchemaFactory,
};

export class DomConverters {
  public static getDOMParser(schema: RtSchema, options: any) {
    const cachedParser = this.domParserMap.get(schema);
    if (cachedParser) return cachedParser;

    const parser = PMDomParser.fromSchema(SCHEMAS[schema](options));
    this.domParserMap.set(schema, parser);
    return parser;
  }

  public static getDOMSerializer(schema: Schema) {
    const cachedSerializer = this.domSerializerMap.get(schema);
    if (cachedSerializer) return cachedSerializer;

    const serializer = DOMSerializer.fromSchema(schema);
    this.domSerializerMap.set(schema, serializer);
    return serializer;
  }

  private static domParserMap = new Map<RtSchema, PMDomParser>();
  private static domSerializerMap = new Map<Schema, DOMSerializer>();
}

/**
 * THIS FUNCTION IS EXECUTED ON BACKEND
 * @param parseHtml
 */
export const htmlToJsonFactory = (parseHtml: (html: string) => Node) => <S extends RtSchema>(
  html: string | Node,
  rtSchema: S,
  options?: Parameters<typeof SCHEMAS[S]>[0],
): { doc: Record<string, any> } | null => {
  const htmlDoc = typeof html === 'string' ? parseHtml(html) : html;
  if (!htmlDoc) return null;

  const parser = DomConverters.getDOMParser(rtSchema, options);
  const pmDoc = parser.parse(htmlDoc);
  return { doc: pmDoc.toJSON() };
};

/**
 * THIS FUNCTION IS EXECUTED ON BACKEND
 * @param getDocument
 */
export const jsonToHtmlFactory = (getDocument: () => Document) => <S extends RtSchema>(
  json: { doc: Record<string, any> },
  rtSchema: S,
  options?: Parameters<typeof SCHEMAS[S]>[0],
): string => {
  const schema = SCHEMAS[rtSchema](options);
  const document = getDocument();

  const contentNode = ProsemirrorNode.fromJSON(schema, json.doc);
  const serializer = DomConverters.getDOMSerializer(schema);
  const domFragment = serializer.serializeFragment(contentNode.content, { document });
  return domFragment.firstChild!.toString();
};

export const htmlToJson = htmlToJsonFactory((html: string) => {
  const { body } = new DOMParser().parseFromString(html, 'text/html');
  return body.firstChild!;
});

export const jsonToHtml = jsonToHtmlFactory(() => document);
